/**
 * Availability state derivation for J-X22 exam windows.
 *
 * Source of truth: `docs/ux/journeys/J-X22-exam-windows.md` § What "availability"
 * means in MVP.
 *
 * The server holds raw timestamps; the client (and the server, when it
 * extends test detail payloads) derives the state. Centralised here so
 * AvailabilityChip, AvailabilityBanner, and any catalogue card share one
 * shape and one set of strings.
 *
 * The five states:
 *
 * | State            | Trigger                                                 |
 * |------------------|---------------------------------------------------------|
 * | always_open      | starts=null AND ends=null                               |
 * | scheduled_future | starts > now                                            |
 * | currently_open   | starts <= now < ends  (or starts<=now and ends=null)    |
 * | closed           | ends <= now                                             |
 * | cooldown         | a candidate-resit_lockouts row gates the candidate      |
 *
 * `cooldown` is candidate-specific and computed elsewhere (it requires the
 * candidate's resit_lockouts row); this helper does NOT decide cooldown.
 */

export type AvailabilityStateKind =
  | 'always_open'
  | 'scheduled_future'
  | 'currently_open'
  | 'closed'
  | 'cooldown';

export interface AvailabilityInput {
  availability_starts_at: string | null;
  availability_ends_at: string | null;
  availability_closed_reason: string | null;
}

export interface AvailabilityState {
  kind: AvailabilityStateKind;
  /** ISO string when relevant (e.g. when state opens or closes). */
  pivot_iso: string | null;
  /** Closed reason text (only set when kind='closed' and reason present). */
  closed_reason: string | null;
}

export function deriveAvailability(
  input: AvailabilityInput,
  now: Date = new Date(),
): AvailabilityState {
  const starts = input.availability_starts_at
    ? new Date(input.availability_starts_at)
    : null;
  const ends = input.availability_ends_at
    ? new Date(input.availability_ends_at)
    : null;

  if (starts === null && ends === null) {
    return { kind: 'always_open', pivot_iso: null, closed_reason: null };
  }

  if (ends !== null && ends.getTime() <= now.getTime()) {
    return {
      kind: 'closed',
      pivot_iso: ends.toISOString(),
      closed_reason: input.availability_closed_reason,
    };
  }

  if (starts !== null && starts.getTime() > now.getTime()) {
    return {
      kind: 'scheduled_future',
      pivot_iso: starts.toISOString(),
      closed_reason: null,
    };
  }

  return {
    kind: 'currently_open',
    pivot_iso: ends?.toISOString() ?? null,
    closed_reason: null,
  };
}

/**
 * Format an ISO timestamp using `Intl.DateTimeFormat` with timezone explicit
 * — J-X22 § Failure mode 3 specifically calls out timezone ambiguity.
 */
export function formatLocalDateTime(
  iso: string,
  locale: string = typeof navigator !== 'undefined' ? navigator.language : 'en-US',
): string {
  const d = new Date(iso);
  // Explicit field options (not dateStyle/timeStyle) so older jsdom builds
  // accept the call. The output shape is still "May 28, 2026, 12:00 PM UTC"
  // depending on the locale — date + time + timezone, never just the time.
  const formatter = new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });
  return formatter.format(d);
}

/**
 * Returns a candidate-facing copy line per the J-X22 state table. The pivot
 * timestamp is formatted with timezone. Returns null for always-open (no
 * message — UI suppresses the chip).
 */
export function availabilityCopy(state: AvailabilityState): string | null {
  switch (state.kind) {
    case 'always_open':
      return null;
    case 'scheduled_future':
      return state.pivot_iso
        ? `Available from ${formatLocalDateTime(state.pivot_iso)}`
        : 'Available soon';
    case 'currently_open':
      return state.pivot_iso
        ? `Available until ${formatLocalDateTime(state.pivot_iso)}`
        : 'Available now';
    case 'closed': {
      const base = 'This test is no longer available';
      return state.closed_reason ? `${base} — ${state.closed_reason}` : base;
    }
    case 'cooldown':
      return state.pivot_iso
        ? `You can retake this test on ${formatLocalDateTime(state.pivot_iso)}`
        : 'Cooldown in effect';
  }
}

/**
 * Returns a relative countdown string ("in 3 days, 4 hours" / "in 2 hours").
 * Used by the AvailabilityBanner on close-to-closing states.
 */
export function relativeCountdown(targetIso: string, now: Date = new Date()): string {
  const target = new Date(targetIso).getTime();
  const diffMs = target - now.getTime();
  if (diffMs <= 0) return 'now';
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 60) return `in ${diffMin} min`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) {
    const remMin = diffMin % 60;
    return remMin > 0
      ? `in ${diffH}h ${remMin}m`
      : `in ${diffH}h`;
  }
  const diffD = Math.floor(diffH / 24);
  const remH = diffH % 24;
  return remH > 0 ? `in ${diffD}d ${remH}h` : `in ${diffD}d`;
}
